import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LockReset as ResetPasswordIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import TransliteratedInput from '../components/TransliteratedInput';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const PathasalaManagement = () => {
  const { accessToken, refreshAccessToken, user } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [pathasalas, setPathasalas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentPathasala, setCurrentPathasala] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    pathasalaName: '',
    address: '',
    phone: '',
    email: '',
    nirvaahakaFirstName: '',
    nirvaahakaLastName: ''
  });

  // States for new user creation scenario
  const [isNewUser, setIsNewUser] = useState(false);
  const [showEmailCheckMessage, setShowEmailCheckMessage] = useState(false);
  const [fieldReadOnly, setFieldReadOnly] = useState({
    email: false,
    phone: false
  });

  // persistent success label state (shown on page)

  const labels = {
    telugu: {
      title: 'పాఠశాల నిర్వహణ',
      addNew: 'కొత్త పాఠశాల చేర్చు',
      pathasalaName: 'పాఠశాల పేరు',
      address: 'చిరునామా',
      phone: 'ఫోన్ నంబరు',
      email: 'ఇ-మెయిల్',
      nirvaahakaName: 'నిర్వాహక పేరు',
      firstName: 'మొదటి పేరు',
      lastName: 'చివరి పేరు',
      actions: 'చర్యలు',
      active: 'సక్రియం',
      inactive: 'నిష్క్రియం',
      save: 'భద్రపరచు',
      cancel: 'రద్దు చేయి',
      edit: 'సవరించు',
      delete: 'తొలగించు',
      activate: 'సక్రియం చేయి',
      deactivate: 'నిష్క్రియం చేయి',
      resetPassword: 'పాస్‌వర్డ్ రీసెట్ చేయి',
      addPathasala: 'పాఠశాల చేర్చు',
      editPathasala: 'పాఠశాల సవరించు'
    },
    sanskrit: {
      title: 'पाठशाला व्यवस्थापनम्',
      addNew: 'नवीनं पाठशालां योजयतु',
      pathasalaName: 'पाठशाला नाम',
      address: 'पता',
      phone: 'दूरभाष',
      email: 'विद्युत्पत्रम्',
      nirvaahakaName: 'निर्वाहक नाम',
      firstName: 'प्रथम नाम',
      lastName: 'अन्तिम नाम',
      actions: 'कार्याणि',
      active: 'सक्रियम्',
      inactive: 'निष्क्रियम्',
      save: 'रक्षतु',
      cancel: 'निरस्तम्',
      edit: 'सम्पादयतु',
      delete: 'विलोपयतु',
      activate: 'सक्रियं करोतु',
      deactivate: 'निष्क्रियं करोतु',
      resetPassword: 'गुप्तशब्दं पुनः स्थापयतु',
      addPathasala: 'पाठशालां योजयतु',
      editPathasala: 'पाठशालां सम्पादयतु'
    }
  };

  const t = labels[lang];

  const fetchPathasalas = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/pathasala');
      const data = await response.json();

      if (data.success) {
        setPathasalas(data.data);
        setError('');
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError('Failed to fetch Pathasalas');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []); // Only recreate if accessToken or refreshAccessToken changes

  useEffect(() => {
    // Page is accessible to everyone
    // If user is logged in, prepopulate email/phone and make them readonly
    if (user && (user.email || user.phone)) {
      setFieldReadOnly({
        email: !!user.email,
        phone: !!user.phone
      });
    }

    // Fetch pathasalas only if user is authenticated
    if (user && ['Admin', 'SuperUser', 'PathashalaAdmin'].includes(user.role)) {
      fetchPathasalas();
    } else {
      // For non-admin users or anonymous, just clear the loading state
      setLoading(false);
    }
  }, [user, fetchPathasalas]);

  const handleOpenDialog = (pathasala = null) => {
    if (pathasala) {
      setEditMode(true);
      setCurrentPathasala(pathasala);
      setFormData({
        pathasalaName: pathasala.pathasalaName,
        address: pathasala.address,
        phone: pathasala.phone,
        email: pathasala.email,
        nirvaahakaFirstName: pathasala.nirvaahakaFirstName,
        nirvaahakaLastName: pathasala.nirvaahakaLastName
      });
      setIsNewUser(false);
      setShowEmailCheckMessage(false);
    } else {
      // Creating new pathasala
      setEditMode(false);
      setCurrentPathasala(null);
      
      // Prepopulate email/phone if user is logged in
      const initialData = {
        pathasalaName: '',
        address: '',
        phone: user?.phone || '',
        email: user?.email || '',
        nirvaahakaFirstName: user?.firstName || '',
        nirvaahakaLastName: user?.lastName || ''
      };
      setFormData(initialData);
      setIsNewUser(false);
      setShowEmailCheckMessage(false);
    }
    setOpenDialog(true);
    setError('');
    setSuccess('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setCurrentPathasala(null);
    setFormData({
      pathasalaName: '',
      address: '',
      phone: '',
      email: '',
      nirvaahakaFirstName: '',
      nirvaahakaLastName: ''
    });
    setIsNewUser(false);
    setShowEmailCheckMessage(false);
    setError('');
  };

  const handleSubmit = async () => {
    try {
      // Validate
      if (!formData.pathasalaName || !formData.address || !formData.phone || 
          !formData.email || !formData.nirvaahakaFirstName || !formData.nirvaahakaLastName) {
        setError('All fields are required');
        return;
      }

      console.log('handleSubmit: Starting submission...');
      const url = editMode 
        ? `/api/pathasala/${currentPathasala._id}` 
        : '/api/pathasala';

      const response = editMode 
        ? await api.put(url, formData)
        : await api.post(url, formData);

      console.log('handleSubmit: Response received, status:', response.status);
      
      let data;
      try {
        data = await response.json();
        console.log('handleSubmit: Parsed JSON data:', data);
      } catch (parseError) {
        console.error('handleSubmit: JSON parse error:', parseError);
        throw new Error('Invalid response format from server');
      }

      if (data.success) {
        console.log('handleSubmit: Success! isNewUser:', data.isNewUser);

        // Close dialog and show a persistent on-page success label.
        // If this was a newly created pathasala for a new user, show bilingual instruction to check email.
        if (!editMode && data.isNewUser) {
          const teluguMsg = (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {'ఇమెయిల్‌లో లాగిన్ వివరాలను తనిఖీ చేయండి'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {'ప్రస్తుత సంవత్సరపు వార్షిక పరీక్షల కోసం వటు రిజిస్ట్రేషన్లు సమర్పించడం ప్రారంభించండి'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                {'(మీ టెంపరరీ పాస్‌వర్డ్ ఇమెయిల్‌లో పంపబడింది)'}
              </Typography>
            </Box>
          );

          const sanskritMsg = (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {'इमेल् मध्ये लोगिन् विवरणानि परीक्षतु'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {'वर्तमान-वर्षस्य वार्षिक-परीक्षणानां हेतु वातु-पंजीकरणानि समर्पयितुम् आरभत'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                {'(अस्थायी गुप्तशब्दः इमेल् मध्ये प्रेषितः अस्ति)'}
              </Typography>
            </Box>
          );

          setSuccess(
            lang === 'telugu'
              ? teluguMsg
              : sanskritMsg
          );

        } else {
          const message = editMode 
            ? (lang === 'telugu' ? 'పాఠశాల విజయవంతంగా నవీకరించబడింది' : 'पाठशाला सफलतापूर्वक अद्यतन किया गया')
            : (lang === 'telugu' ? 'పాఠశాల విజయవంతంగా సృష్టించబడింది' : 'पाठशाला सफलतापूर्वक बनाया गया');

          setSuccess(message);
        }

        setError(''); // Clear any previous errors
        handleCloseDialog();

        // Only fetch if user is authenticated
        if (user && ['Admin', 'SuperUser', 'PathashalaAdmin'].includes(user.role)) {
          console.log('handleSubmit: Fetching updated pathasalas');
          fetchPathasalas();
        }

        // Show temp password in development
        if (data.data?.tempPassword) {
          console.log('Temporary Password:', data.data.tempPassword);
        }
      } else {
        // Show error in dialog, keep it open
        console.log('handleSubmit: API returned success=false, message:', data.message);
        const errorMsg = data.message || (lang === 'telugu' ? 'పాఠశాలను సేవ్ చేయడంలో విఫలమైంది' : 'पाठशाला को सहेजने में विफल');
        setError(errorMsg);
      }
    } catch (error) {
      // Show error in dialog, keep it open
      console.error('handleSubmit: Caught error:', error);
      const errorMsg = lang === 'telugu' 
        ? 'పాఠశాలను సేవ్ చేయడంలో విఫలమైంది' 
        : 'पाठशाला को सहेजने में विफल';
      setError(errorMsg);
      console.error('Error details:', error);
    }
  };

  const handleToggleStatus = async (id, currentStatus, pathasalaName) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} ${pathasalaName}?`)) {
      return;
    }

    try {
      const response = await api.patch(`/api/pathasala/${id}/toggle-status`);
      const data = await response.json();

      if (data.success) {
        setSuccess(`Pathasala ${action}d successfully`);
        fetchPathasalas();
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError(`Failed to ${action} Pathasala`);
      console.error('Error:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this Pathasala?')) {
      return;
    }

    try {
      const response = await api.delete(`/api/pathasala/${id}`);
      const data = await response.json();

      if (data.success) {
        setSuccess('Pathasala deactivated successfully');
        fetchPathasalas();
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError('Failed to delete Pathasala');
      console.error('Error:', error);
    }
  };

  const handleResetPassword = async (id, email, pathasalaName) => {
    if (!window.confirm(`Reset password for ${pathasalaName}? A new password will be sent to ${email}`)) {
      return;
    }

    try {
      const response = await api.post(`/api/pathasala/${id}/reset-password`);
      const data = await response.json();

      if (data.success) {
        setSuccess('Password reset successfully. Email sent!');
        if (data.tempPassword) {
          console.log('New Temporary Password:', data.tempPassword);
        }
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError('Failed to reset password');
      console.error('Error:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Determine what to show
  const isAdmin = user && ['Admin', 'SuperUser'].includes(user.role);
  const isPathashalaAdmin = user && user.role === 'PathashalaAdmin';
  const visiblePathasalas = isAdmin ? pathasalas : pathasalas.filter(p => 
    p.email === user?.email || p.phone === user?.phone
  );
  const canAddNew = true; // Everyone can add new pathasalas
  const hasPathasalas = isAdmin || isPathashalaAdmin;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#7f4b2d' }}>
          {t.title}
        </Typography>
        {canAddNew && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ bgcolor: '#7f4b2d', '&:hover': { bgcolor: '#001040' } }}
          >
            {t.addNew}
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {showEmailCheckMessage && (
        <Alert severity="info" sx={{ mb: 3 }} onClose={() => {
          setShowEmailCheckMessage(false);
          handleCloseDialog();
        }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {lang === 'telugu' 
              ? 'సందేహాస్పద ఇ-మెయిల్ కోసం దయచేసి మీ ఇన్‌బాక్స్ ను తనిఖీ చేయండి!' 
              : 'कृपया अपने ईमेल के लिए अपने इनबॉक्स की जांच करें!'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {lang === 'telugu'
              ? 'లాగిన్ చేయడానికి తాత్కాలిక పాస్‌వర్డ్ మరియు వ్యక్తిగత సమాచారం కోసం ఇమెయిల్‌ను చదువు.'
              : 'लॉगिन करने के लिए अस्थायी पासवर्ड और व्यक्तिगत विवरण के लिए ईमेल पढ़ें।'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {lang === 'telugu'
              ? 'మీరు తాత్కాలిక పాస్‌వర్డ్ ఆమోదం చేసిన తర్వాత, మీ పాస్‌వర్డ్‌ను వెంటనే మార్చండి.'
              : 'एक बार जब आप अस्थायी पासवर्ड को मंजूरी दे दें, तो तुरंत अपना पासवर्ड बदलें।'}
          </Typography>
        </Alert>
      )}

      {hasPathasalas && visiblePathasalas.length > 0 && (
      <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>{t.pathasalaName}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{t.address}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{t.nirvaahakaName}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Contact</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">{t.actions}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visiblePathasalas.map((pathasala) => {
              const isOwnPathasala = !isAdmin && (pathasala.email === user?.email || pathasala.phone === user?.phone);
              const canManage = isAdmin || isOwnPathasala;
              
              return (
              <TableRow key={pathasala._id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{pathasala.pathasalaName}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocationIcon sx={{ fontSize: 16, color: '#666' }} />
                    {pathasala.address}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PersonIcon sx={{ fontSize: 16, color: '#666' }} />
                    {pathasala.nirvaahakaFirstName} {pathasala.nirvaahakaLastName}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <EmailIcon sx={{ fontSize: 14, color: '#666' }} />
                      <Typography variant="body2">{pathasala.email}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PhoneIcon sx={{ fontSize: 14, color: '#666' }} />
                      <Typography variant="body2">{pathasala.phone}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={pathasala.isActive ? t.active : t.inactive}
                    color={pathasala.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  {canManage && (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Tooltip title={t.edit}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(pathasala)}
                          sx={{ color: '#7f4b2d' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t.resetPassword}>
                        <IconButton
                          size="small"
                          onClick={() => handleResetPassword(pathasala._id, pathasala.email, pathasala.pathasalaName)}
                          sx={{ color: '#ff9800' }}
                        >
                          <ResetPasswordIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {isAdmin && (
                        <>
                          <Tooltip title={pathasala.isActive ? t.deactivate : t.activate}>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleStatus(pathasala._id, pathasala.isActive, pathasala.pathasalaName)}
                              sx={{ color: pathasala.isActive ? '#d32f2f' : '#4caf50' }}
                            >
                              {pathasala.isActive ? <ToggleOffIcon fontSize="small" /> : <ToggleOnIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t.delete}>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(pathasala._id)}
                              sx={{ color: '#d32f2f' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#7f4b2d', color: 'white' }}>
          {editMode ? t.editPathasala : t.addPathasala}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
            <TransliteratedInput
              label={t.pathasalaName}
              value={formData.pathasalaName}
              onChange={(val) => setFormData(prev => ({ ...prev, pathasalaName: val }))}
              onSuggestionSelect={(val) => setFormData(prev => ({ ...prev, pathasalaName: val }))}
              targetScript={lang === 'telugu' ? 'telugu' : 'devanagari'}
              fullWidth
              required
            />

            <TransliteratedInput
              label={t.address}
              value={formData.address}
              onChange={(val) => setFormData(prev => ({ ...prev, address: val }))}
              onSuggestionSelect={(val) => setFormData(prev => ({ ...prev, address: val }))}
              targetScript={lang === 'telugu' ? 'telugu' : 'devanagari'}
              fullWidth
              required
              multiline
              rows={2}
            />
            <TextField
              label={t.phone}
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              fullWidth
              required
              disabled={fieldReadOnly.phone}
              type="tel"
              inputProps={{ maxLength: 10 }}
            />

            <TextField
              label={t.email}
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              fullWidth
              required
              disabled={fieldReadOnly.email || editMode}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TransliteratedInput
                label={t.firstName}
                value={formData.nirvaahakaFirstName}
                onChange={(val) => setFormData(prev => ({ ...prev, nirvaahakaFirstName: val }))}
                onSuggestionSelect={(val) => setFormData(prev => ({ ...prev, nirvaahakaFirstName: val }))}
                targetScript={lang === 'telugu' ? 'telugu' : 'devanagari'}
                fullWidth
                required
              />

              <TransliteratedInput
                label={t.lastName}
                value={formData.nirvaahakaLastName}
                onChange={(val) => setFormData(prev => ({ ...prev, nirvaahakaLastName: val }))}
                onSuggestionSelect={(val) => setFormData(prev => ({ ...prev, nirvaahakaLastName: val }))}
                targetScript={lang === 'telugu' ? 'telugu' : 'devanagari'}
                fullWidth
                required
              />
            </Box>

            {!editMode && (
              <Alert severity="info">
                A temporary password will be generated and sent to the email address.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog}>{t.cancel}</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{ bgcolor: '#7f4b2d', '&:hover': { bgcolor: '#001040' } }}
          >
            {t.save}
          </Button>
        </DialogActions>
      </Dialog>

      {/* No toast; using persistent on-page success label instead */}
    </Box>
  );
};

export default PathasalaManagement;
